/**
 * Cliente do financeiro.
 *
 * Leitura vai pela rede com TanStack Query; escrita vai pelo **outbox**, como
 * o roteiro — criar despesa é uma das operações que a Architecture Review §7.1
 * habilita offline, e um jantar registrado no restaurante sem sinal precisa
 * chegar ao servidor depois.
 */

const API = '/api/v1';

export interface ExpenseRow {
  id: string;
  description: string;
  category: string;
  scope: 'trip' | 'private';
  totalMinor: number;
  currency: string;
  splitMode: string;
  spentAt: string;
  createdBy: string;
  version: number;
}

export interface FinanceSummary {
  currency: string;
  groupTotalMinor: number;
  myShareMinor: number;
  myPaidMinor: number;
  myBalanceMinor: number;
  /** `null` quando a pessoa não está em nenhuma família. */
  family: {
    id: string;
    name: string;
    memberCount: number;
    shareMinor: number;
    paidMinor: number;
    balanceMinor: number;
  } | null;
  byCategory: { category: string; totalMinor: number }[];
}

export interface BalancesResponse {
  currency: string;
  balances: {
    memberId: string;
    paidMinor: number;
    owedMinor: number;
    balanceMinor: number;
  }[];
  transfers: { from: string; to: string; amount: number }[];
  settlements: {
    id: string;
    fromMemberId: string;
    toMemberId: string;
    amountMinor: number;
    settledAt: string | null;
  }[];
}

export interface ExpenseDetail {
  expense: ExpenseRow & {
    subtotalMinor: number;
    taxMinor: number;
    tipMinor: number;
  };
  payments: { payerMemberId: string; amountMinor: number }[];
  splits: {
    beneficiaryMemberId: string | null;
    amountMinor: number;
    subtotalMinor: number;
    taxMinor: number;
    tipMinor: number;
  }[];
  items: { id: string; description: string; amountMinor: number }[];
  confirmations: { memberId: string; state: string; note: string | null }[];
}

async function ler<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar (${res.status})`);
  return (await res.json()) as T;
}

export const listExpenses = (tripId: string) =>
  ler<{ expenses: ExpenseRow[] }>(`${API}/trips/${tripId}/expenses`);

/** Resumo e saldos vêm sempre na moeda da viagem — o servidor decide, o cliente só lê. */
export const readSummary = (tripId: string) => ler<FinanceSummary>(`${API}/trips/${tripId}/finance/summary`);

export const readBalances = (tripId: string) => ler<BalancesResponse>(`${API}/trips/${tripId}/finance/balances`);

export const readExpense = (tripId: string, expenseId: string) =>
  ler<ExpenseDetail>(`${API}/trips/${tripId}/expenses/${expenseId}`);
